import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { request, getPercentage, formatDuration } from '../../../services/utilities';
import { departmentAPI, staffAPI } from '../../../services/constants';
import { startBlock, stopBlock } from '../../../actions/redux-block';
import { notifyError } from '../../../services/notify';
import {
	PieChart,
	Pie,
	Cell,
	Tooltip,
	Legend,
	LineChart,
	XAxis,
	YAxis,
	Line,
	LabelList,
	Area,
	AreaChart,
	ResponsiveContainer,
	CartesianGrid,
} from 'recharts';
import TableLoading from '../../../components/TableLoading';
import ResponsiveBarChart from '../ResponsiveBarChart';
import upperCase from 'lodash.uppercase';
import moment from 'moment';
import AnalysisBystaff from '../../../components/Modals/frontDesk/staffAnalyticsmodal';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const TimeRangeDropdown = ({ value, onChange, chartId }) => {
	return (
		<select
			value={value}
			onChange={e => onChange(e.target.value, chartId)}
			className="form-control form-control-sm rounded"
			style={{ width: '160px', marginBottom: '10px', borderRadius: '50px' }}
		>
			<option value="today">Today</option>
			<option value="pastWeek">Past Week</option>
			<option value="past30Days">Past 30 Days</option>
			<option value="lastMonth">Last Month</option>
			<option value="pastQuarter">Past Quarter</option>
			<option value="pastSixMonths">Past Six Months</option>
			<option value="pastYear">Past Year</option>
			<option value="pastThreeYears">Past Three Years</option>
			<option value="allTime">All Time</option>
		</select>
	);
};

const HROverview = ({ selectedStaff }) => {
	const containerRef = useRef(null);
	const [loaded, setLoaded] = useState(false);
	const [stats, setStats] = useState(null);
	const [departments, setDepartments] = useState([]);

	const dispatch = useDispatch();

	const fetchStaffs = useCallback(async () => {
		try {
			dispatch(startBlock());

			const url1 = `${departmentAPI}?page=1&limit=100&term=&include_stats=1&has_appointment=`;
			const rs1 = await request(url1, 'GET', true);
			const { result: deptResult } = rs1;
			setDepartments(deptResult?.map(d => ({ name: upperCase(d.name), count: d.staff_count })));

			const url = `${staffAPI}?page=1&limit=5&q=&status=&include_stats=1&include_resignation_stats=1&include_gender_stats=1`;
			const rs = await request(url, 'GET', true);
			const { stats: statsData } = rs;
			window.scrollTo({ top: 0, behavior: 'smooth' });
			if (deptResult[0]) {
				const { has_unit_count, total_unknown_count, total_male_count, total_female_count, department_count, total_unit_count } =
					deptResult[0];
				setStats({
					...stats,
					...statsData,
					has_unit_count,
					total_unknown_count,
					total_male_count,
					total_female_count,
					department_count,
					total_unit_count,
				});
			} else {
				setStats({ ...stats, ...statsData });
			}
			setLoaded(true);
			dispatch(stopBlock());
		} catch (error) {
			notifyError('error fetching staffs');
			setLoaded(true);
			dispatch(stopBlock());
		}
	}, [dispatch]);

	useEffect(() => {
		if (!loaded) {
			fetchStaffs();
		}
	}, [fetchStaffs, loaded]);

	const getStatPercentage = type => {
		switch (type) {
			case 'total':
				return Number(100 - getPercentage(stats?.totalRegistered, stats?.totalRegistered)).toFixed(1) || 0;
			case 'active':
				return Number(100 - getPercentage(stats?.totalRegistered, stats?.totalActive)).toFixed(1) || 0;
			case 'inactive':
				return Number(100 - getPercentage(stats?.totalRegistered, stats?.totalInActive)).toFixed(1) || 0;
			case 'onleave':
				return Number(100 - getPercentage(stats?.totalRegistered, stats?.totalOnLeave)).toFixed(1) || 0;
			default:
				return 0;
		}
	};

	const getDeptStatPercentage = type => {
		const allStaff = (stats?.total_unknown_count || 0) + (stats?.total_female_count || 0) + (stats?.total_male_count || 0);
		switch (type) {
			case 'total':
				return Number(100 - getPercentage(stats?.department_count, stats?.department_count)).toFixed(1) || 0;
			case 'units':
				return Number(100 - getPercentage(stats?.department_count, stats?.total_unit_count)).toFixed(1) || 0;
			case 'has-units':
				return Number(100 - getPercentage(stats?.department_count, stats?.has_unit_count)).toFixed(1) || 0;
			case 'has-no-units':
				return Number(100 - getPercentage(stats?.department_count, stats?.department_count - stats?.has_unit_count)).toFixed(1) || 0;
			case 'female':
				return Number(100 - getPercentage(allStaff, stats?.total_female_count)).toFixed(1) || 0;
			case 'male':
				return Number(100 - getPercentage(allStaff, stats?.total_male_count)).toFixed(1) || 0;
			case 'unknown':
				return Number(100 - getPercentage(allStaff, stats?.total_unknown_count)).toFixed(1) || 0;
			default:
				return 0;
		}
	};

	const [timeRanges, setTimeRanges] = useState({
		departmentActivity: 'past30Days',
		departmentMinutes: 'past30Days',
		staffActivityTrend: 'past30Days',
		staffAverageActivityTime: 'past30Days',
		metrics: 'past30Days',
	});

	// Loading states for each chart
	const [loadingStates, setLoadingStates] = useState({
		departmentActivity: false,
		departmentMinutes: false,
		staffActivityTrend: false,
		staffAverageActivityTime: false,
		metrics: false,
	});

	// Data states for each chart
	const [departmentActivity, setDepartmentActivity] = useState([]);
	const [departmentMinutes, setDepartmentMinutes] = useState([]);
	const [staffActivityTrend, setStaffActivityTrend] = useState([]);
	const [staffAverageActivityTime, setStaffAverageActivityTime] = useState([]);

	// const mapTimeRangeToApiFilter = range => {
	// 	switch (range) {
	// 		case 'today':
	// 			return 'today';
	// 		case 'pastWeek':
	// 			return 'past-week';
	// 		case 'past30Days':
	// 			return 'past-month';
	// 		case 'pastYear':
	// 			return 'past-year';
	// 		case 'allTime':
	// 			return 'all-time';
	// 		default:
	// 			return 'past-month';
	// 	}
	// };

	const mapTimeRangeToApiFilter = range => {
		switch (range) {
			case 'today':
				return 'today';
			case 'pastWeek':
				return 'past-week';
			case 'past30Days':
				return 'past-month';
			case 'lastMonth':
				return 'last-month';
			case 'pastQuarter':
				return 'past-quarter';
			case 'pastSixMonths':
				return 'past-six-months';
			case 'pastYear':
				return 'past-year';
			case 'pastThreeYears':
				return 'past-three-years';
			case 'allTime':
				return 'all-time';
			default:
				return 'past-month';
		}
	};

	const fetchDepartmentActivity = async range => {
		setLoadingStates(prev => ({ ...prev, departmentActivity: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilter(range);
			const res = await request(`analytics/deparment-activity?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiResponse = res?.result
				?.sort((a, b) => b.total - a.total)
				?.sort(() => Math.random() - 0.5)
				?.map(x => ({
					name: x?.department_name,
					value: Math.round(parseFloat(x?.timeSpentSeconds)),
				}))
				.slice(0, 20);
			setDepartmentActivity(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch department activity:', error);
			setDepartmentActivity([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, departmentActivity: false }));
		}
	};

	const fetchDepartmentMinutes = async range => {
		setLoadingStates(prev => ({ ...prev, departmentMinutes: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilter(range);
			const res = await request(`analytics/deparment-minutes?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiResponse = res?.result
				?.sort(() => Math.random() - 0.5)
				?.map(x => ({
					name: x?.department,
					value: Math.round(parseFloat(x?.averageTimeSpent)),
				}));
			setDepartmentMinutes(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch department minutes:', error);
			setDepartmentMinutes([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, departmentMinutes: false }));
		}
	};

	const fetchStaffActivityTrend = async range => {
		setLoadingStates(prev => ({ ...prev, staffActivityTrend: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilter(range);
			const res = await request(`analytics/staff-activity-trends?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name:
					x?.date ||
					(dateFilter == 'past-three-years' && x?.month && moment(x?.month).format('MMMM, YY')) ||
					(x?.month && moment(x?.month).format('MMMM')) ||
					x?.day ||
					x?.week ||
					x?.year ||
					x?.hour,
				value: Math.round(parseFloat(x?.count)),
			}));
			setStaffActivityTrend(apiResponse);
		} catch (error) {
			console.error('Failed to fetch staff activity trend:', error);
			setStaffActivityTrend([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, staffActivityTrend: false }));
		}
	};

	const fetchStaffAverageActivityTime = async range => {
		setLoadingStates(prev => ({ ...prev, staffAverageActivityTime: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilter(range);
			const res = await request(`analytics/staff-activity?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name: x?.username,
				value: Math.round(parseFloat(x?.avgTimeSpent)),
			}));
			setStaffAverageActivityTime(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch staff average activity time:', error);
			setStaffAverageActivityTime([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, staffAverageActivityTime: false }));
		}
	};

	const handleTimeRangeChange = (range, chartId) => {
		setTimeRanges(prev => ({ ...prev, [chartId]: range }));

		switch (chartId) {
			case 'departmentActivity':
				fetchDepartmentActivity(range);
				break;
			case 'departmentMinutes':
				fetchDepartmentMinutes(range);
				break;
			case 'staffActivityTrend':
				fetchStaffActivityTrend(range);
				break;
			case 'staffAverageActivityTime':
				fetchStaffAverageActivityTime(range);
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		fetchDepartmentActivity(timeRanges.departmentActivity);
		fetchDepartmentMinutes(timeRanges.departmentMinutes);
		fetchStaffActivityTrend(timeRanges.staffActivityTrend);
		fetchStaffAverageActivityTime(timeRanges.staffAverageActivityTime);
	}, []);

	const renderCustomLabelAlt = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString()}
			</text>
		);
	};

	const renderCustomLabel = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{formatDuration(value)}
			</text>
		);
	};

	return (
		<div className="content-i">
			<div className="content-box">
				<div className="row">
					<div className="col-sm-8">
						<div className="element-wrapper">
							<h6 className="element-header">Staff Data</h6>
							<div className="row">
								<div className="col-sm-3">
									<a className="element-box el-tablo" href="#">
										<div className="label">Total</div>
										<div className="value">{stats?.totalRegistered || '0'}</div> <br />
										<div className={`trending trending-${getStatPercentage('total') < 10 ? 'up' : 'down'}-basic`}>
											<span>{stats ? getStatPercentage('total') : '0'}%</span>
											<i className="os-icon os-icon-arrow-down" />
										</div>
									</a>
								</div>
								<div className="col-sm-3">
									<a className="element-box el-tablo" href="#">
										<div className="label">Active</div>
										<div className="value">{stats?.totalActive || '0'}</div> <br />
										<div className={`trending trending-${getStatPercentage('active') < 10 ? 'up' : 'down'}-basic`}>
											<span>{stats ? getStatPercentage('active') : '0'}%</span>
											<i className="os-icon os-icon-arrow-down" />
										</div>
									</a>
								</div>
								<div className="col-sm-3">
									<a className="element-box el-tablo" href="#">
										<div className="label">Departments</div>
										<div className="value">{stats?.department_count || '0'}</div>
										<br />
										<div className={`trending trending-${+getDeptStatPercentage('units') < 10 ? 'up' : 'down'}-basic`}>
											<span>{stats ? getDeptStatPercentage('total') : '0'}%</span>
											<i className="os-icon os-icon-arrow-down" />
										</div>
									</a>
								</div>
								<div className="col-sm-3">
									<a className="element-box el-tablo" href="#">
										<div className="label">Units</div>
										<div className="value">{stats?.total_unit_count || '0'}</div>
										<br />
										<div className={`trending trending-${+getDeptStatPercentage('units') > 50 ? 'up' : 'down'}-basic`}>
											<span>{stats ? getDeptStatPercentage('units') : '0'}%</span>
											<i className="os-icon os-icon-arrow-down" />
										</div>
									</a>
								</div>
							</div>
							<div className="row">
								<div className="col-sm-3">
									<a className="element-box el-tablo" href="#">
										<div className="label">Inactive</div>
										<div className="value">{stats?.totalInActive || '0'}</div>
										<br />
										<div className={`trending trending-${getStatPercentage('inactive') < 10 ? 'up' : 'down'}-basic`}>
											<span>{stats ? getStatPercentage('inactive') : '0'}%</span>
											<i className="os-icon os-icon-arrow-down" />
										</div>
									</a>
								</div>
								<div className="col-sm-3">
									<a className="element-box el-tablo" href="#">
										<div className="label">On Leave</div>
										<div className="value">{stats?.totalOnLeave || '0'}</div> <br />
										<div className={`trending trending-${getStatPercentage('onleave') < 10 ? 'up' : 'down'}-basic`}>
											<span>{stats ? getStatPercentage('onleave') : '0'}%</span>
											<i className="os-icon os-icon-arrow-up2" />
										</div>
									</a>
								</div>
								<div className="col-sm-3">
									<a className="element-box el-tablo" href="#">
										<div className="label">Has Units</div>
										<div className="value">{stats?.has_unit_count || '0'}</div>
										<br />
										<div className={`trending trending-${+getDeptStatPercentage('has-units') > 50 ? 'up' : 'down'}-basic`}>
											<span>{stats ? getDeptStatPercentage('has-units') : '0'}%</span>
											<i className="os-icon os-icon-arrow-down" />
										</div>
									</a>
								</div>
								<div className="col-sm-3">
									<a className="element-box el-tablo" href="#">
										<div className="label">Has No Units</div>
										<div className="value">{(stats?.department_count || 0) - (stats?.has_unit_count || 0)}</div>
										<br />
										<div className={`trending trending-${+getDeptStatPercentage('has-no-units') > 50 ? 'up' : 'down'}-basic`}>
											<span>{stats ? getDeptStatPercentage('has-no-units') : '0'}%</span>
											<i className="os-icon os-icon-arrow-down" />
										</div>
									</a>
								</div>
							</div>
						</div>
					</div>

					<div className="col-sm-4">
						<div className="element-wrapper">
							<h6 className="element-header">Gender Distribution</h6>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											<div className="d-flex justify-content-center">
												{loaded ? (
													<PieChart width={300} height={250}>
														<Pie
															data={stats?.genderData}
															innerRadius={20}
															outerRadius={80}
															fill="#8884d8"
															paddingAngle={0}
															dataKey="value"
															label
														>
															{stats?.genderData.map((entry, index) => (
																<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
															))}
														</Pie>
														<Tooltip />
														<Legend />
													</PieChart>
												) : (
													<TableLoading />
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Departments data</h6>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loaded ? (
												<div ref={containerRef} className="d-flex justify-content-center p-3">
													<ResponsiveBarChart data={departments} dataKey="count" fill="#0088FE" />
												</div>
											) : (
												<TableLoading />
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Resignation Trends By Role</h6>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loaded ? (
												<div ref={containerRef} className="d-flex justify-content-center p-3">
													<ResponsiveBarChart data={stats?.resignationRoleData} dataKey="count" fill="#0088FE" />
												</div>
											) : (
												<TableLoading />
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Resignation Trends By Month</h6>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loaded ? (
												<div ref={containerRef} className="d-flex justify-content-center p-3">
													<ResponsiveBarChart data={stats?.resignationData} dataKey="count" fill="#0088FE" />
												</div>
											) : (
												<TableLoading />
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="content-box">
					<div className="row">
						<div className="col-sm-12">
							<div className="element-wrapper">
								<h6 className="element-header">Department Activity</h6>
								<div className="d-flex justify-content-end w-100">
									<TimeRangeDropdown value={timeRanges.departmentMinutes} onChange={handleTimeRangeChange} chartId="departmentMinutes" />
								</div>
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									This chart visualizes the volume of activities carried out by each department over the selected time range. It helps
									identify which departments are the most or least active.
								</p>
								<div className="element-box">
									{loadingStates.departmentMinutes ? (
										<TableLoading />
									) : departmentMinutes.length > 0 ? (
										<div style={{ width: '100%', height: 400 }}>
											<ResponsiveContainer>
												<LineChart data={departmentMinutes} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
													<CartesianGrid strokeDasharray="3 3" />
													<XAxis
														dataKey={'name'}
														angle={-45}
														textAnchor="end"
														fontSize={12}
														interval={0}
														tickFormatter={value => {
															const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
															const capitalized = truncated
																.toLowerCase()
																.split(' ')
																.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																.join(' ');
															return capitalized;
														}}
													/>
													<YAxis domain={[0, 'auto']} />
													<Tooltip
														formatter={value => Number(value).toLocaleString()}
														labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
													/>
													<Legend wrapperStyle={{ marginBottom: '-50px' }} />
													<Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} name="Value" connectNulls>
														<LabelList
															dataKey="value"
															position="top"
															content={renderCustomLabelAlt}
															style={{ fontSize: '12px', fill: '#333' }}
														/>
													</Line>
												</LineChart>
											</ResponsiveContainer>
										</div>
									) : (
										<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
											<p className=" mt-6" style={{ paddingTop: '50px' }}>
												No Record for the selected time
											</p>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>

					<div className="row">
						<div className="col-sm-12">
							<div className="element-wrapper">
								<h6 className="element-header">Staff Average Activity Time </h6>
								<div className="d-flex justify-content-end w-100">
									<TimeRangeDropdown
										value={timeRanges.staffAverageActivityTime}
										onChange={handleTimeRangeChange}
										chartId="staffAverageActivityTime"
									/>
								</div>
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									Illustrates the average duration (in minutes) that staff members spend per activity. It highlights staff efficiency and
									helps identify areas needing support or improvement.
								</p>
								<div className="element-box">
									{loadingStates.staffAverageActivityTime ? (
										<TableLoading />
									) : staffAverageActivityTime.length > 0 ? (
										<div style={{ height: '400px' }}>
											<ResponsiveContainer>
												<AreaChart data={staffAverageActivityTime.slice(0, 20)} margin={{ top: 20, right: 20, left: 10, bottom: 20 }}>
													<CartesianGrid strokeDasharray="3 3" />
													<XAxis
														dataKey={'name'}
														angle={-45}
														textAnchor="end"
														fontSize={12}
														interval={0}
														tickFormatter={value => {
															const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
															const capitalized = truncated
																.toLowerCase()
																.split(' ')
																.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																.join(' ');
															return capitalized;
														}}
													/>
													<YAxis domain={[0, 'auto']} />
													<Tooltip
														formatter={value => formatDuration(value)}
														labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
													/>
													{/* <Legend /> */}
													<Area
														type="monotone"
														dataKey="value"
														stroke="#8884d8"
														fill="#8884d8"
														strokeWidth={2}
														dot={{ r: 4 }}
														activeDot={{ r: 6 }}
													>
														<LabelList
															dataKey="value"
															content={renderCustomLabel}
															position="top"
															style={{ fontSize: '12px', fill: '#333' }}
														/>
													</Area>
												</AreaChart>
											</ResponsiveContainer>
										</div>
									) : (
										<div className="text-center fs-bold">No Record Found!</div>
									)}
								</div>
							</div>
						</div>
					</div>

					<div className="row">
						<div className="col-sm-12">
							<div className="element-wrapper">
								<h6 className="element-header">Department Minutes Overview</h6>
								<div className="d-flex justify-content-end w-100">
									<TimeRangeDropdown value={timeRanges.departmentActivity} onChange={handleTimeRangeChange} chartId="departmentActivity" />
								</div>
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									Displays the average time (in minutes) each department spends handling its activities. Useful for spotting time-intensive
									departments and optimizing workflows.
								</p>
								<div className="element-box">
									{loadingStates.departmentActivity ? (
										<TableLoading />
									) : departmentActivity.length > 0 ? (
										<div style={{ width: '100%', height: 400 }}>
											<ResponsiveContainer>
												<LineChart data={departmentActivity} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
													<CartesianGrid strokeDasharray="3 3" />
													<XAxis
														dataKey={'name'}
														angle={-45}
														// textAnchor="end"
														fontSize={12}
														height={100}
														style={{ textAnchor: 'end' }}
														// interval={0}
														tickFormatter={value => {
															const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
															const capitalized = truncated
																.toLowerCase()
																.split(' ')
																.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																.join(' ');
															return capitalized;
														}}
													/>
													<YAxis domain={[0, 'auto']} />
													<Tooltip
														formatter={value => formatDuration(value)}
														labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
													/>
													{/* <Legend /> */}
													<Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} name="Value" connectNulls>
														<LabelList
															dataKey="value"
															position="top"
															content={({ value, x, y }) => (
																<text x={x} y={y - 5} fill="#333" fontSize={12} textAnchor="middle">
																	{formatDuration(value)}
																</text>
															)}
															style={{ fontSize: '12px', fill: '#333' }}
														/>
													</Line>
												</LineChart>
											</ResponsiveContainer>
										</div>
									) : (
										<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
											<p className=" mt-6" style={{ paddingTop: '50px' }}>
												No Record for the selected time
											</p>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>

					<div className="row">
						<div className="col-sm-12">
							<div className="element-wrapper">
								<h6 className="element-header">Staff Activity Trends</h6>
								<div className="d-flex justify-content-end w-100">
									<TimeRangeDropdown value={timeRanges.staffActivityTrend} onChange={handleTimeRangeChange} chartId="staffActivityTrend" />
								</div>
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
									Tracks prescription volumes over time to analyze patient demand and operational efficiency.
								</p>
								<div className="element-box">
									{loadingStates.staffActivityTrend ? (
										<TableLoading />
									) : staffActivityTrend.length > 0 ? (
										<div style={{ height: '400px' }}>
											<ResponsiveContainer>
												<AreaChart data={staffActivityTrend} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
													<CartesianGrid strokeDasharray="3 3" />
													<XAxis
														dataKey={'name'}
														angle={-45}
														textAnchor="end"
														fontSize={12}
														interval={0}
														tickFormatter={value => {
															const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
															const capitalized = truncated
																.toLowerCase()
																.split(' ')
																.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																.join(' ');
															return capitalized;
														}}
													/>
													<YAxis domain={[0, 'auto']} />
													<Tooltip
														formatter={value => Number(value).toLocaleString()}
														labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
													/>
													<Legend wrapperStyle={{ marginBottom: '-45px' }} />
													<Area
														type="monotone"
														dataKey="value"
														stroke="#8884d8"
														fill="#8884d8"
														strokeWidth={2}
														dot={{ r: 4 }}
														activeDot={{ r: 6 }}
													>
														<LabelList
															dataKey="value"
															content={renderCustomLabelAlt}
															position="top"
															style={{ fontSize: '12px', fill: '#333' }}
														/>
													</Area>
												</AreaChart>
											</ResponsiveContainer>
										</div>
									) : (
										<div className="text-center fs-bold">No Record Found!</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default HROverview;
